import { useCallback, useEffect, useRef, useState } from 'react';
import { getReferrals } from '@/services/referrals.service.js';

// Loads the full referral list for one partner (all statuses) plus the
// active-partner list and the server-computed counts. The screen filters by
// status client-side so the count pills always reflect every bucket.
//
// Pass no slug on first mount (partner === null) and the hook holds off the
// fetch until the caller picks a partner — avoids an all-partners scan and the
// per-row FAA/CRM auto-checks running before we know which partner to show.
export function useReferrals(partnerSlug) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    if (!partnerSlug) {
      // No partner selected yet — surface partners only, no rows.
      setLoading(true);
      try {
        const d = await getReferrals({});
        if (!aborted.current) { setData(d); setError(null); }
      } catch (err) {
        if (!aborted.current) setError(err);
      } finally {
        if (!aborted.current) setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const d = await getReferrals({ partner: partnerSlug, status: 'all' });
      if (!aborted.current) { setData(d); setError(null); }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [partnerSlug]);

  useEffect(() => {
    aborted.current = false;
    refresh();
    return () => { aborted.current = true; };
  }, [refresh]);

  return {
    partners: data?.partners || [],
    rows: data?.rows || [],
    counts: data?.counts || null,
    staleAfterDays: data?.stale_after_days ?? 14,
    error,
    loading,
    refresh,
  };
}
